import {
  Literal,
  Null,
  Optional,
  Array as RArray,
  Boolean as RBoolean,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { UserMcpServerConnectionId } from "../../idTypeBrands.js";
import type { ThreadAutoApproveMcpToolIdentity } from "../../mcp-client/callMcpToolAgentArgs.js";
import { McpClientToolName } from "../../mcp-client/toolConfig.js";

import { TOOL_NAMES } from "./toolNames.js";

/** Identity for the persistent "Always allow this tool" choice. */
export const AskUserMcpToolApprovalContext = RRecord({
  mcpServerConnectionId: UserMcpServerConnectionId,
  mcpToolName: McpClientToolName,
});
export type AskUserMcpToolApprovalContext = Static<
  typeof AskUserMcpToolApprovalContext
>;

/** Display type when an agent wants the user to confirm or deny something. */
export const AskUserConfirmDisplay = RRecord({
  type: Literal("confirm"),
  description: String,
  confirmText: Optional(String),
  denyText: Optional(String),
});
export type AskUserConfirmDisplay = Static<typeof AskUserConfirmDisplay>;

/** Display type when an agent wants the user to approve an MCP tool call. */
export const AskUserMcpToolApprovalDisplay = RRecord({
  type: Literal("mcp-tool-approval"),
  description: String,
  denyText: Optional(String),
  mcpToolApprovalContext: AskUserMcpToolApprovalContext,
});
export type AskUserMcpToolApprovalDisplay = Static<
  typeof AskUserMcpToolApprovalDisplay
>;

/** Display type when an agent needs the user to reauthenticate an OAuth data connection. */
export const AskUserOAuthDisplay = RRecord({
  type: Literal("oauth"),
  description: String,
  dataConnectionId: String,
  dataConnectionName: String,
  dataConnectionType: String,
  oAuthConnectionId: String,
  authWithSso: RBoolean,
  currentRole: Optional(String.Or(Null)),
  requiredRole: Optional(String.Or(Null)),
});
export type AskUserOAuthDisplay = Static<typeof AskUserOAuthDisplay>;

/** Display type when an agent needs the user to reauthenticate an MCP server. */
export const AskUserMcpReauthDisplay = RRecord({
  type: Literal("mcp-reauth"),
  mcpServerName: String,
  mcpServerConnectionId: UserMcpServerConnectionId,
});
export type AskUserMcpReauthDisplay = Static<typeof AskUserMcpReauthDisplay>;

/** Display type for a multiple-choice question presented to the user. */
export const AskUserMultipleChoiceDisplay = RRecord({
  type: Literal("multiple-choice"),
  question: String,
  options: RArray(RRecord({ label: String, description: Optional(String) })),
  allowOther: Optional(RBoolean),
});
export type AskUserMultipleChoiceDisplay = Static<
  typeof AskUserMultipleChoiceDisplay
>;

/** Display type when an agent wants the user to review an onboarding eval draft. */
export const AskUserEvalReviewDisplay = RRecord({
  type: Literal("eval-review"),
  description: String,
  /**
   * Persisted drafts may predate the current `EvalOnboardingDefinition`
   * schema, so readers must parse this and handle failure.
   */
  definition: Unknown,
});
export type AskUserEvalReviewDisplay = Static<typeof AskUserEvalReviewDisplay>;

export const AskUserConfirmResponse = Union(
  Literal("CONFIRM"),
  Literal("DENY"),
  Literal("EDITED"),
);
export type AskUserConfirmResponse = Static<typeof AskUserConfirmResponse>;

/** Sentinel string written into answers[] when the user doesn't respond in time. */
export const USER_QUESTION_TIMED_OUT = "User did not respond in time";

/**
 * Payload published by `respondToAskUserQuestionRequest` when the user answered
 * a multiple-choice question.
 */
export type QuestionAnsweredPayload = {
  type: "question-answered";
  answer: string;
};

/**
 * Payload published by `respondToAskUserQuestionRequest` when the user dismissed
 * a multiple-choice question (closed it without picking an option).
 */
export type QuestionDismissedPayload = {
  type: "question-dismissed";
};

/** Tagged payload shape used for ask-user question responses on the pub/sub wire. */
export type QuestionTopicPayload =
  | QuestionAnsweredPayload
  | QuestionDismissedPayload;

/**
 * Payload shape used for confirm/oauth ask responses on the pub/sub wire.
 *
 * Publishers should send the tagged-object variants. The bare-string
 * `"CONFIRM"`/`"DENY"`/`"EDITED"` forms are accepted by consumers for
 * backwards compatibility with publishers running pre-tagged-payload code
 * and are scheduled for removal after the 5-release deprecation window.
 * The `EDITED` variant carries an optional, tool-defined `editedEntityId`
 * the client can return (e.g. the id of a resource it just created) so the
 * tool's `onEdited` handler can reference it back to the agent.
 */
export type ConfirmTopicPayload =
  /** @deprecated Publishers should send `{ type: "CONFIRM" }`. Kept for wire compatibility. */
  | "CONFIRM"
  /** @deprecated Publishers should send `{ type: "DENY" }`. Kept for wire compatibility. */
  | "DENY"
  /** @deprecated Publishers should send `{ type: "EDITED" }`. Kept for wire compatibility. */
  | "EDITED"
  | { type: "CONFIRM" }
  | { type: "DENY" }
  | { type: "EDITED"; editedEntityId?: string };

/**
 * Wire type for `AgentAskUserResponseTopic`. Both the confirm and the question
 * resolvers publish tagged objects; consumers narrow with `.type`.
 */
export type AskUserTopicPayload = ConfirmTopicPayload | QuestionTopicPayload;

/**
 * User requests or "Ask User" messages represent a request from the agent to the user (e.g., such as permission
 * to continue executing the tool).
 */
export const AskUserDisplay = Union(
  AskUserConfirmDisplay,
  AskUserMcpToolApprovalDisplay,
  AskUserOAuthDisplay,
  AskUserMcpReauthDisplay,
  AskUserMultipleChoiceDisplay,
  AskUserEvalReviewDisplay,
);
export type AskUserDisplay = Static<typeof AskUserDisplay>;

/**
 * Approval mode is pinned per run, not per thread: it is resolved when a prompt
 * is submitted and cannot change until that run ends. It determines what the
 * agent does when it hits a HITL check requiring approval (ex: confirm/deny).
 *
 * ASK_FOR_APPROVAL (default): The agent should ask the user for approval.
 * SKIP_APPROVALS: The agent should skip the approval and continue executing the tool.
 */
export const AgentApprovalMode = Union(
  Literal("ASK_FOR_APPROVAL"),
  Literal("SKIP_APPROVALS"),
);
export type AgentApprovalMode = Static<typeof AgentApprovalMode>;

/**
 * A block that represents the agent asking the user for permission to continue execution.
 *
 * This is a transient intermediary block similar to tool progress updates which is not
 * actually sent to the agent BUT could result in a "tool rejected by user" response which is.
 */
export const MessageAskUserBlock = RRecord({
  type: Literal("ask_user"),
  request: AskUserDisplay,
});
export type MessageAskUserBlock = Static<typeof MessageAskUserBlock>;

/**
 * Key under which a tool's per-thread auto-approve flag is stored in
 * {@link AskUserAutoApprovals}. Only produce values via
 * {@link getThreadAutoApproveKey} so the read and write sides agree.
 */
export const ThreadAutoApproveKey = String.withBrand("ThreadAutoApproveKey");
export type ThreadAutoApproveKey = Static<typeof ThreadAutoApproveKey>;

/**
 * Per-thread auto-approve map. Keys are usually a tool name, but for
 * `CallMcpTool` the key is a composite produced by {@link getThreadAutoApproveKey}
 * so "allow for this thread" can be scoped to a single MCP tool rather than all MCP tools.
 */
export type AskUserAutoApprovals = Partial<
  Record<ThreadAutoApproveKey, boolean>
>;

/**
 * Canonical derivation of the key used to read and write a tool's per-thread
 * auto-approve flag in {@link AskUserAutoApprovals}.
 *
 * For most tools this is just the tool name. For MCP it returns a composite
 * key for the tool so "allow for this thread" does not implicitly approve every
 * MCP tool. If the identity is missing it falls back to the bare tool name, but
 * approval checks deliberately ignore that legacy broad MCP key.
 */
export function getThreadAutoApproveKey(
  toolName: string,
  mcpTool?: ThreadAutoApproveMcpToolIdentity | null,
): ThreadAutoApproveKey {
  if (toolName === TOOL_NAMES.CALL_MCP_TOOL && mcpTool != null) {
    return ThreadAutoApproveKey.check(
      `${toolName}::${JSON.stringify([
        mcpTool.mcpServerType,
        mcpTool.mcpServerName.toLowerCase(),
        mcpTool.mcpToolName,
      ])}`,
    );
  }
  return ThreadAutoApproveKey.check(toolName);
}

export function isToolAutoApprovedForThread(
  autoApprovals: AskUserAutoApprovals | null,
  toolName: string,
  mcpTool?: ThreadAutoApproveMcpToolIdentity | null,
): boolean {
  if (autoApprovals == null) {
    return false;
  }
  const key = getThreadAutoApproveKey(toolName, mcpTool);
  // Ignore legacy blanket MCP approvals; MCP approval is per-tool now.
  if (key === TOOL_NAMES.CALL_MCP_TOOL) {
    return false;
  }
  return autoApprovals[key] === true;
}
